import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';

/**
 * Directive to enforce a maximum character limit on content-editable elements.
 *
 * Usage:
 * - Apply `appMaxChars` to any content-editable element.
 * - Optionally set `[maxChars]` to define a custom limit (default is 600).
 * - Emits custom events:
 *   - `maxCharLimitReached`: boolean indicating if the limit is reached.
 *   - `charCount`: number indicating current character count.
 *
 * Features:
 * - Prevents input beyond the limit.
 * - Restores previous text if limit exceeded.
 * - Maintains cursor position after correction.
 * - Allows configurable class annotation via `[maxCharsClass]`.
 */

@Directive({
  selector: '[appMaxChars]',
  exportAs: 'appMaxChars',
  standalone: true,
})
export class MaxCharsDirective implements OnInit {
  @Input() maxChars = 600;
  private _previousText = '';
  private _caretOffset = 0;
  private _lastInputType = '';

  constructor(private el: ElementRef<HTMLElement>) { }

  ngOnInit(): void {
    this._previousText = this.el.nativeElement.innerHTML || '';
    this._caretOffset = 0;
    this.el.nativeElement.addEventListener(
      'beforeinput',
      (event: InputEvent) => {
        this.onBeforeInput(event);
      }
    );
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    this.inputChanged();
  }

  public inputChanged(): void {
    const textLength = this.getTextLength();
    const element = this.el.nativeElement;

    // Let native undo/redo history progress without directive rollback.
    const isHistoryAction =
      this._lastInputType === 'historyUndo' || this._lastInputType === 'historyRedo';

    const isPasteFromCopiedText = this._lastInputType === 'insertFromPaste' || this._lastInputType === 'insertFromDrop';

    if (!isHistoryAction && !isPasteFromCopiedText && textLength > this.maxChars) {
      const previousLength = this.getTextLengthFromHtml(this._previousText);
      if (textLength >= previousLength) {
        // Content grew or stayed the same while over limit — revert
        element.innerHTML = this._previousText;
        this.restoreCursorOffset(this._caretOffset);
        this.dispatchBooleanEvent('maxCharLimitReached', true);
        this.dispatchNumberEvent('charCount', previousLength);
        return;
      }
    }

    this._previousText = element.innerHTML;
    this.dispatchBooleanEvent('maxCharLimitReached', textLength >= this.maxChars);
    this.dispatchNumberEvent('charCount', textLength);
  }

  /**
 * Handles the `beforeinput` event to intercept user input before it is applied.
 * Saves the current caret position and snapshot of HTML content.
 * Prevents disallowed input types when the character limit has been reached.
 * History actions (Ctrl+Z / Ctrl+Y) are always permitted.
 */
  onBeforeInput(event: InputEvent): void {
    const textLength = this.getTextLength();
    this._lastInputType = event.inputType || '';

    const allowedTypes = [
      'deleteContentBackward',
      'deleteContentForward',
      'deleteByCut',
      'insertLineBreak',
      'insertParagraph',
      'historyUndo',
      'historyRedo',
    ];

    // Always let browser history actions proceed (Ctrl+Z / Ctrl+Y)
    if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
      return;
    }

    // Block only non-allowed edits when at/over max
    if (textLength >= this.maxChars && !allowedTypes.includes(event.inputType)) {
      this._caretOffset = this.getCursorOffset();
      event.preventDefault();
      return;
    }

    this._previousText = this.el.nativeElement.innerHTML;
    this._caretOffset = this.getCursorOffset();
  }

  /**
   * Calculates the current cursor position (offset) in characters
   * within the content-editable element (_messageBox).
   * It creates a range from the start of the element to the current
   * cursor position and returns the length of the text in that range.
   */
  private getCursorOffset(): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(this.el.nativeElement);
    preRange.setEnd(range.endContainer, range.endOffset);

    return preRange.toString().length;
  }

  /**
   * Restores the cursor to a specific character offset within the
   * content-editable element (_messageBox). It traverses the DOM tree
   * to find the exact text node and position that matches the given offset,
   * then sets the cursor there.
   */
  private restoreCursorOffset(offset: number): void {
    const element = this.el.nativeElement;
    const selection = window.getSelection();
    const range = document.createRange();

    let charIndex = 0;
    const nodeStack: Node[] = [element];
    let node: Node | undefined;
    let found = false;

    while (!found && nodeStack.length > 0) {
      node = nodeStack.pop();
      if (!node) continue;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = (node.textContent || '').length;
        if (charIndex + textLength >= offset) {
          range.setStart(node, offset - charIndex);
          range.collapse(true);
          found = true;
          break;
        } else {
          charIndex += textLength;
        }
      } else {
        let i = node.childNodes.length;
        while (i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }

    if (found && selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  private getTextLength(): number {
    const count = this.customLength(this.el.nativeElement);
    return count;
  }

  /** Computes the effective character length of an HTML string using the same
   *  rules as getTextLength(), without touching the live DOM. */
  private getTextLengthFromHtml(html: string): number {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return this.customLength(tmp);
  }

  private customLength(element: HTMLElement): number {
    let length = 0;

    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        length += node.textContent?.length ?? 0;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName.toLowerCase() === 'sabis-equation-wrapper') {
          length += 1;
        } else {
          length += this.customLength(el); // Recurse into non-span elements
        }
      }
    });

    return length;
  }

  private dispatchBooleanEvent(name: string, value: boolean): void {
    const event = new CustomEvent(name, { detail: value, bubbles: true });
    this.el.nativeElement.dispatchEvent(event);
  }

  private dispatchNumberEvent(name: string, value: number): void {
    const event = new CustomEvent(name, { detail: value, bubbles: true });
    this.el.nativeElement.dispatchEvent(event);
  }

  /**
   * Like inputChanged() but skips the truncation step.
   * Used when content is inserted programmatically and may legally exceed maxChars
   * (e.g. a bot-message selection copied into the input).
   * The host component is responsible for gating the send action.
   */
  public inputChangedAllowOverflow(): void {
    const textLength = this.getTextLength();
    const element = this.el.nativeElement;
    // Accept whatever is in the element as the new baseline
    this._previousText = element.innerHTML;
    // Inform listeners of the real count and limit state
    this.dispatchBooleanEvent('maxCharLimitReached', textLength >= this.maxChars);
    this.dispatchNumberEvent('charCount', textLength);
  }
}
